import { Component, OnInit, Inject, HostListener } from '@angular/core';
import { Angulartics2 } from 'angulartics2';
import { IInputFile, IWizardFile } from 'app/files/file.model';
import { UIDialogData } from 'app/ui/dialog/dialog-injector';
import { UIDialogService } from 'app/ui/dialog/dialog.service';
import { UploadSettings } from './upload-settings.model';
import { UserSettingsService } from 'app/user/user-settings.service';
import { WizardCreateComponent } from 'app/orders/wizard/create/create.component';
import { WizardService } from 'app/orders/wizard/wizard.service';
import { camelCase } from 'app/core/case-transform';
import { OrderSettings } from 'app/orders/settings/settings.model';
import { OrderSettingsDcItemModel } from 'app/orders/settings-dc/settings-dc.model';
import { PersonalizedLocalStorageService } from 'app/notifications/personalized-local-storage.service';

@Component({
  selector: 'wizard-upload',
  templateUrl: 'upload.component.html',
  styleUrls: ['upload.component.scss'],
  preserveWhitespaces: true
})
export class WizardUploadComponent implements OnInit {
  files: IWizardFile[];

  get uploadedFiles(): number {
    return this._wizardService.statistics.files;
  }

  get skipCreateDialog(): boolean {
    return this._settings.skipCreateDialog;
  }

  get showCreateDialog(): boolean {
    return !this.skipCreateDialog;
  }

  get canShowSettings(): boolean {
    return this._settings.showSettings;
  }

  get canStartProcess(): boolean {
    return !this.isLoading && this.filesReady();
  }

  orderSettings: OrderSettings;
  orderSettingsDc: OrderSettingsDcItemModel[];
  error: string;
  errorDescription: string;
  isLoading: boolean = false;
  private templateError: string = 'Error';
  private templateErrorDescription: string =
    'Error of processing of parameters';

  constructor(
    @Inject(UIDialogData)
    private _settings: UploadSettings,
    private _angulartics2: Angulartics2,
    private _dialogService: UIDialogService,
    private _wizardService: WizardService,
    private _userSettingsService: UserSettingsService,
    private _personalizedLocalStorageService: PersonalizedLocalStorageService
  ) { }

  ngOnInit(): void {
    this.files = this._wizardService.files;
    const taskPropertiesLocalStorage = this._personalizedLocalStorageService.getJson(
      'order_properties'
    );
    if (taskPropertiesLocalStorage && taskPropertiesLocalStorage.length > 0) {
      this.orderSettingsDc = Object.assign([], taskPropertiesLocalStorage);
    } else {
      this.orderSettingsDc = [];
    }
    this.orderSettings = Object.assign(
      {},
      this._userSettingsService.orderSettings
    );
  }

  showSettings() {
    this._openSettingsDialog();
  }

  next() {
    this._openSettingsDialog();
  }

  filesReady(): boolean {
    return this._wizardService.filesReady();
  }

  startProcess(): void {
    this._createOrder();
  }

  removeFile(file: IWizardFile): void {
    this._wizardService.removeFile(file);
  }

  uploadFiles(files: IInputFile[]): void {
    this._wizardService.upload(files);
  }

  private _createOrder() {
    this.onHideError();
    this.isLoading = true;
    let data: any = {};
    try {
      if (this.isIc()) {
        (data.verification = this.orderSettings.verificationType),
          (data.export_format = this.orderSettings.exportFormat);
        data.region = this.orderSettings.region;
      } else {
        const _temp = this.orderSettingsDc.reduce((acc, cur, _i) => {
          if (cur.name && cur.name !== '') {
            acc[cur.name] = cur.value || '';
          }
          return acc;
        }, {});
        Object.assign(data, _temp);
      }
      this._wizardService
        .create(data, this._userSettingsService.serviceType)
        .subscribe(
          order => {
            this._personalizedLocalStorageService.setJson(
              'order_properties',
              this.orderSettingsDc
            );
            const properties = {
              event: 'Create Order',
              'Service Type': order.serviceType,
              'Document Count': this._wizardService.files.length,
              'Export Format': this.orderSettings.exportFormat,
              'Invoice Region': this.orderSettings.region,
              'Verification Type': this.orderSettings.verificationType,
              Number: order.number
            };
            if (!this.isIc()) {
              delete properties['Invoice Region'];
            }
            this._angulartics2.eventTrack.next({
              action: 'Create Order',
              properties: properties
            });
            this._wizardService.reset();
            this._dialogService.close();
          },
          error => this._onError(error)
        );
    } catch (error) {
      data = {};
      this.error = this.templateError;
      this.errorDescription = this.templateErrorDescription;
      console.error(error);
      this.isLoading = false;
    }
  }

  isIc(): boolean {
    return this._userSettingsService.serviceType === 'ic';
  }

  onHideError(): void {
    this.error = null;
    this.errorDescription = null;
  }

  private _onError(response): void {
    if (response.status === 400) {
      const responseJson = response.json();
      this.error = camelCase(responseJson.error);
      this.errorDescription = this._getErrorDescription(responseJson);
    } else {
      this.error = this.templateError;
      this.errorDescription = this.templateErrorDescription;
    }
    this.isLoading = false;
  }

  private _getErrorDescription(responseJson): string {
    const modelState = responseJson.modelState;
    if (!modelState) {
      return responseJson.errorDescription;
    }

    return Object.keys(modelState)
      .map(fieldName => modelState[fieldName].join('\n'))
      .join('\n');
  }

  private _openSettingsDialog() {
    this._settings.isFilesReady = this.filesReady();
    this._dialogService.open(WizardCreateComponent, { data: this._settings });
  }

  @HostListener('document:keydown', ['$event'])
  onKeydownHandler(event: KeyboardEvent) {
    if (event.keyCode === 13) {
      if (this.skipCreateDialog) {
        if (this.canStartProcess) {
          this.startProcess();
        }
      } else {
        if (this.filesReady()) {
          this.next();
        }
      }
    }
  }
}
